import * as React from "react";
import {
  BrowserModalState,
  BrowserModalAction,
  ColumnType,
  ColumnState,
  BreadcrumbItem,
  BrowserData,
} from "../types";

const initialColumnState: ColumnState = {
  selectedHref: undefined,
  filter: "",
  focusedIndex: -1, // -1 means no focus, only show focus during keyboard navigation
};

const initialState: BrowserModalState = {
  columns: {
    services: { ...initialColumnState },
    regions: { ...initialColumnState },
    types: { ...initialColumnState },
    items: { ...initialColumnState },
    children: { ...initialColumnState },
    nestedItems: { ...initialColumnState },
  },
  breadcrumbs: [],
  selections: [],
  activeColumn: "services",
};

const columnOrder: ColumnType[] = ["services", "regions", "types", "items", "children", "nestedItems"];

function getColumnsAfter(column: ColumnType): ColumnType[] {
  const index = columnOrder.indexOf(column);
  return columnOrder.slice(index + 1);
}

function browserReducer(state: BrowserModalState, action: BrowserModalAction): BrowserModalState {
  switch (action.type) {
    case "SELECT_ITEM": {
      const columnsAfter = getColumnsAfter(action.column);
      const clearedColumns = columnsAfter.reduce(
        (acc, col) => ({
          ...acc,
          [col]: { ...initialColumnState },
        }),
        {}
      );

      // Build new breadcrumbs up to and including the selected item
      const columnIndex = columnOrder.indexOf(action.column);
      const newBreadcrumbs: BreadcrumbItem[] = state.breadcrumbs
        .filter((b) => columnOrder.indexOf(b.type) < columnIndex)
        .concat({
          level: columnIndex,
          type: action.column,
          label: action.label,
          href: action.href,
        });

      return {
        ...state,
        columns: {
          ...state.columns,
          [action.column]: {
            ...state.columns[action.column],
            selectedHref: action.href,
          },
          ...clearedColumns,
        },
        breadcrumbs: newBreadcrumbs,
        activeColumn: action.column,
      };
    }

    case "SET_FILTER":
      return {
        ...state,
        columns: {
          ...state.columns,
          [action.column]: {
            ...state.columns[action.column],
            filter: action.filter,
            focusedIndex: -1, // Reset focus when filter changes
          },
        },
      };

    case "SET_FOCUSED_INDEX":
      return {
        ...state,
        columns: {
          ...state.columns,
          [action.column]: {
            ...state.columns[action.column],
            focusedIndex: action.index,
          },
        },
      };

    case "SET_ACTIVE_COLUMN":
      return {
        ...state,
        activeColumn: action.column,
      };

    case "CLEAR_AFTER_COLUMN": {
      const columnsAfter = getColumnsAfter(action.column);
      const clearedColumns = columnsAfter.reduce(
        (acc, col) => ({
          ...acc,
          [col]: { ...initialColumnState },
        }),
        {}
      );

      // Remove breadcrumbs after this column
      const columnIndex = columnOrder.indexOf(action.column);
      const newBreadcrumbs = state.breadcrumbs.filter(
        (b) => columnOrder.indexOf(b.type) <= columnIndex
      );

      return {
        ...state,
        columns: {
          ...state.columns,
          ...clearedColumns,
        },
        breadcrumbs: newBreadcrumbs,
      };
    }

    case "ADD_SELECTION":
      return {
        ...state,
        selections: [...state.selections, action.item],
      };

    case "REMOVE_SELECTION":
      return {
        ...state,
        selections: state.selections.filter((s) => s._cpln.ref !== action.ref),
      };

    case "TOGGLE_SELECTION": {
      const exists = state.selections.some((s) => s._cpln.ref === action.item._cpln.ref);
      if (exists) {
        return {
          ...state,
          selections: state.selections.filter((s) => s._cpln.ref !== action.item._cpln.ref),
        };
      }
      return {
        ...state,
        selections: [...state.selections, action.item],
      };
    }

    case "SET_SINGLE_SELECTION": {
      const exists = state.selections.some((s) => s._cpln.ref === action.item._cpln.ref);
      if (exists) {
        // Deselect if already selected
        return {
          ...state,
          selections: [],
        };
      }
      return {
        ...state,
        selections: [action.item],
      };
    }

    case "CLEAR_SELECTIONS":
      return {
        ...state,
        selections: [],
      };

    case "NAVIGATE_TO_BREADCRUMB": {
      const targetBreadcrumb = state.breadcrumbs[action.index];
      if (!targetBreadcrumb) return state;

      // Clear all columns after the target
      const columnsAfter = getColumnsAfter(targetBreadcrumb.type);
      const clearedColumns = columnsAfter.reduce(
        (acc, col) => ({
          ...acc,
          [col]: { ...initialColumnState },
        }),
        {}
      );

      // Trim breadcrumbs to include only up to the clicked one
      const newBreadcrumbs = state.breadcrumbs.slice(0, action.index + 1);

      return {
        ...state,
        columns: {
          ...state.columns,
          ...clearedColumns,
        },
        breadcrumbs: newBreadcrumbs,
        activeColumn: targetBreadcrumb.type,
      };
    }

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

export function useBrowserState() {
  const [state, dispatch] = React.useReducer(browserReducer, initialState);

  const selectItem = React.useCallback(
    (column: ColumnType, href: string, label: string) => {
      dispatch({ type: "SELECT_ITEM", column, href, label });
    },
    []
  );

  const setFilter = React.useCallback((column: ColumnType, filter: string) => {
    dispatch({ type: "SET_FILTER", column, filter });
  }, []);

  const setFocusedIndex = React.useCallback((column: ColumnType, index: number) => {
    dispatch({ type: "SET_FOCUSED_INDEX", column, index });
  }, []);

  const setActiveColumn = React.useCallback((column: ColumnType) => {
    dispatch({ type: "SET_ACTIVE_COLUMN", column });
  }, []);

  const clearAfterColumn = React.useCallback((column: ColumnType) => {
    dispatch({ type: "CLEAR_AFTER_COLUMN", column });
  }, []);

  const toggleSelection = React.useCallback((item: BrowserData) => {
    dispatch({ type: "TOGGLE_SELECTION", item });
  }, []);

  const setSingleSelection = React.useCallback((item: BrowserData) => {
    dispatch({ type: "SET_SINGLE_SELECTION", item });
  }, []);

  const clearSelections = React.useCallback(() => {
    dispatch({ type: "CLEAR_SELECTIONS" });
  }, []);

  const navigateToBreadcrumb = React.useCallback((index: number) => {
    dispatch({ type: "NAVIGATE_TO_BREADCRUMB", index });
  }, []);

  const reset = React.useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  return {
    state,
    dispatch,
    selectItem,
    setFilter,
    setFocusedIndex,
    setActiveColumn,
    clearAfterColumn,
    toggleSelection,
    setSingleSelection,
    clearSelections,
    navigateToBreadcrumb,
    reset,
  };
}
